import React, { useMemo, useId, useEffect, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  FileText,
  Calculator,
  Users,
  PieChart,
  Percent,
  ShoppingCart
} from "lucide-react";

/* ---------- Small viewport hook ---------- */
const useWindowWidth = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return w;
};

/* ---------- Helpers ---------- */
const toNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  return parseFloat(String(v).replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
};

const getYearMonth = (row) => {
  if (row?.Rok && row?.Miesiac) return `${row.Rok}-${String(row.Miesiac).padStart(2, "0")}`;
  const d = row?.DataZakupu ? new Date(row.DataZakupu) : null;
  if (!d || isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const prevYm = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const lastJpkDate = (rows) => {
  let latest = null;
  for (const r of rows) {
    const candidate = r?.DataWytworzeniaJPK || r?.DataZakupu || r?.DataWplywu;
    if (!candidate) continue;
    const d = new Date(candidate);
    if (!isNaN(d) && (!latest || d > latest)) latest = d;
  }
  return latest;
};

const fmtPLN0 = (n) =>
  (n ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " zł";

const deltaPct = (curr, prev) => {
  if (prev === 0) return curr > 0 ? 100 : 0;
  return ((curr - prev) / prev) * 100;
};
const fmtPct = (v) => (isFinite(v) ? (v >= 0 ? "+" : "") + v.toFixed(1) + "%" : "—");
const pct = (v) => (isFinite(v) ? (v * 100).toFixed(0) + "%" : "—");

const topNShare = (list, n = 3) => {
  const total = list.reduce((s, x) => s + x, 0);
  if (total <= 0) return 0;
  const sorted = [...list].sort((a, b) => b - a);
  const top = sorted.slice(0, n).reduce((s, x) => s + x, 0);
  return top / total;
};

/* Values for Zakupy */
const netFromRow = (row) => toNumber(row.K_42);
const vatInFromRow = (row) => toNumber(row.K_43);

/* ---------- Sparkline ---------- */
const Sparkline = ({ data = [], color = "#2563eb", height = 36, opacity = 0.12, strokeOpacity = 0.8 }) => {
  const gid = useId();
  const n = data.length;
  if (!n) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const h = height;
  const pad = 2;
  const vbw = Math.max(1, n - 1);

  const y = (v) => {
    const t = (v - min) / range;
    const yy = h - pad - t * (h - pad * 2);
    return Math.max(pad, Math.min(h - pad, yy));
  };

  let line = `M 0 ${y(data[0])}`;
  for (let i = 1; i < n; i++) line += ` L ${i} ${y(data[i])}`;
  const area = `${line} L ${n - 1} ${h} L 0 ${h} Z`;

  return (
    <svg viewBox={`0 0 ${vbw} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height }}>
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={opacity} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="0.12" strokeOpacity={strokeOpacity} />
    </svg>
  );
};

/* ---------- Tiny card UI ---------- */
const SectionHeader = ({ title = "Zakupy", icon: Icon = ShoppingCart, compact = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: compact ? 6 : 8 }}>
    <div
      style={{
        width: compact ? 30 : 36,
        height: compact ? 30 : 36,
        borderRadius: 10,
        background: "#f1f5f9",
        display: "grid",
        placeItems: "center",
      }}
    >
      <Icon size={compact ? 16 : 18} />
    </div>
    <div style={{ fontSize: compact ? "0.95rem" : "1rem", fontWeight: 800, color: "#0f172a" }}>{title}</div>
  </div>
);

const Separator = ({ compact = false }) => (
  <div style={{ height: 1, width: "100%", background: "#e5e7eb", marginBottom: compact ? 6 : 10 }} />
);

const Box = ({ children, sparkline, compact = false }) => (
  <div
    style={{
      position: "relative",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      background: "#fff",
      boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
      padding: compact ? 12 : 16,
      minHeight: compact ? 84 : 90,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      gap: compact ? 6 : 8,
      overflow: "hidden",
    }}
  >
    <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    {sparkline && (
      <div style={{ position: "absolute", left: 8, right: 8, bottom: compact ? 6 : 8, zIndex: 0 }}>{sparkline}</div>
    )}
  </div>
);

const TitleRow = ({ icon, label, hintColor = "#64748b", compact = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, color: hintColor, fontSize: compact ? 11 : 12, fontWeight: 700 }}>
    {icon}
    <span>{label}</span>
  </div>
);

const ValueRow = ({ value, delta, color = "#111827", mm = true, compact = false, invertForPurchases = false }) => {
  const up = (delta ?? 0) >= 0;
  const trendIsPositive = invertForPurchases ? !up : up;
  const Arrow = trendIsPositive ? TrendingUp : TrendingDown;
  const deltaColor = trendIsPositive ? "#16a34a" : "#dc2626";

  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
      <div style={{ fontSize: compact ? 20 : 22, fontWeight: 800, color }}>{value}</div>
      {delta != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: deltaColor, fontSize: compact ? 11 : 12, fontWeight: 700 }}>
          <Arrow size={compact ? 14 : 16} />
          {fmtPct(delta)} {mm && <span style={{ color: "#94a3b8", fontWeight: 500 }}>&nbsp;m/m</span>}
        </div>
      )}
    </div>
  );
};

/* ---------- Main component ---------- */
const ZakupyKPI = ({ purchasesData = [] }) => {
  const width = useWindowWidth();
  const compact = width < 520;

  const result = useMemo(() => {
    const byYm = new Map();
    purchasesData.forEach((r) => {
      const ym = getYearMonth(r);
      if (!ym) return;
      if (!byYm.has(ym)) byYm.set(ym, []);
      byYm.get(ym).push(r);
    });
    const yms = [...byYm.keys()].sort();
    const ymCurr = yms.length ? yms[yms.length - 1] : null;
    const ymPrev = ymCurr ? prevYm(ymCurr) : null;

    const rowsCurr = ymCurr ? byYm.get(ymCurr) || [] : [];
    const rowsPrev = ymPrev ? byYm.get(ymPrev) || [] : [];

    // Timeseries
    const monthlyNet = yms.map((ym) => (byYm.get(ym) || []).reduce((s, r) => s + netFromRow(r), 0));
    const monthlyInv = yms.map((ym) => {
      const set = new Set();
      (byYm.get(ym) || []).forEach((r) => r?.DowodZakupu && set.add(r.DowodZakupu));
      return set.size;
    });
    const monthlyVatIn = yms.map((ym) => (byYm.get(ym) || []).reduce((s, r) => s + vatInFromRow(r), 0));

    // Aggregates
    const currNet = rowsCurr.reduce((s, r) => s + netFromRow(r), 0);
    const prevNet = rowsPrev.reduce((s, r) => s + netFromRow(r), 0);
    const netDelta = deltaPct(currNet, prevNet); // invert in UI

    const uniq = (arr, key) => {
      const set = new Set();
      arr.forEach((r) => r?.[key] && set.add(r[key]));
      return set.size;
    };
    const currInv = uniq(rowsCurr, "DowodZakupu");
    const prevInv = uniq(rowsPrev, "DowodZakupu");
    const invDelta = deltaPct(currInv, prevInv); // invert in UI

    const vatInCurr = rowsCurr.reduce((s, r) => s + vatInFromRow(r), 0);
    const vatInPrev = rowsPrev.reduce((s, r) => s + vatInFromRow(r), 0);
    const vatInDelta = deltaPct(vatInCurr, vatInPrev); // regular, ↑ good

    // Dostawcy – liczba dostawców w bieżącym miesiącu (+% m/m)
    const setCurrSup = new Set(rowsCurr.map((r) => r.NrDostawcy || r.NazwaDostawcy).filter(Boolean));
    const setPrevSup = new Set(rowsPrev.map((r) => r.NrDostawcy || r.NazwaDostawcy).filter(Boolean));
    const currSuppliers = setCurrSup.size;
    const prevSuppliers = setPrevSup.size;
    const suppliersDelta = deltaPct(currSuppliers, prevSuppliers); // regular, ↑ is good (większa dywersyfikacja)

    // Nowi dostawcy – pierwszy raz w historii (nie było ich NIGDY wcześniej)
    // current
    const allBeforeCurr = new Set();
    yms.filter((m) => m < (ymCurr || "0000-00")).forEach((m) => {
      (byYm.get(m) || []).forEach((r) => {
        const k = r.NrDostawcy || r.NazwaDostawcy;
        if (k) allBeforeCurr.add(k);
      });
    });
    const currNewEver = [...setCurrSup].filter((k) => !allBeforeCurr.has(k)).length;

    // previous (for % m/m porównania nowi-dostawcy)
    let prevNewEver = 0;
    if (ymPrev) {
      const allBeforePrev = new Set();
      yms.filter((m) => m < ymPrev).forEach((m) => {
        (byYm.get(m) || []).forEach((r) => {
          const k = r.NrDostawcy || r.NazwaDostawcy;
          if (k) allBeforePrev.add(k);
        });
      });
      const setPrevMonthSup = new Set(rowsPrev.map((r) => r.NrDostawcy || r.NazwaDostawcy).filter(Boolean));
      prevNewEver = [...setPrevMonthSup].filter((k) => !allBeforePrev.has(k)).length;
    }
    const newSuppliersDelta = deltaPct(currNewEver, prevNewEver); // regular, ↑ is neutral/positive

    // Mix PL/EU
    let pl = 0, eu = 0;
    rowsCurr.forEach((r) => {
      const n = netFromRow(r);
      if (!r.KodKrajuNadaniaTIN || r.KodKrajuNadaniaTIN === "PL") pl += n;
      else eu += n;
    });
    const total = pl + eu || 1;
    const mix = { pl: pl / total, eu: eu / total };

    // Koncentracja Top-3
    const amountsBySupplier = new Map();
    rowsCurr.forEach((r) => {
      const key = r.NrDostawcy || r.NazwaDostawcy || "—";
      const val = netFromRow(r);
      amountsBySupplier.set(key, (amountsBySupplier.get(key) || 0) + val);
    });
    const top3 = topNShare([...amountsBySupplier.values()], 3);

    const asOfDate = lastJpkDate(purchasesData);
    const slice12 = (arr) => arr.slice(-12);

    return {
      asOfDate,
      currNet, netDelta,
      currInv, invDelta,
      vatInCurr, vatInDelta,
      currSuppliers, suppliersDelta,
      currNewEver, prevNewEver, newSuppliersDelta,
      mix, top3,
      sparkNet: slice12(monthlyNet),
      sparkInv: slice12(monthlyInv),
      sparkVatIn: slice12(monthlyVatIn),
    };
  }, [purchasesData]);

  const {
    asOfDate,
    currNet, netDelta,
    currInv, invDelta,
    vatInCurr, vatInDelta,
    currSuppliers, suppliersDelta,
    currNewEver, newSuppliersDelta,
    mix, top3,
    sparkNet, sparkInv, sparkVatIn,
  } = result;

  const green = "#16a34a";
  const red = "#dc2626";
  const blue = "#2563eb";
  const black = "#111827";

const asOfText = asOfDate
  ? `Stan na miesiąc ${asOfDate.toLocaleString("pl-PL", { month: "long" })} ${asOfDate.getFullYear()}, na podstawie ostatniego pliku JPK`
  : `Stan na miesiąc — , na podstawie ostatniego pliku JPK`;

  const gridStyle = {
    display: "grid",
    gap: 12,
    gridTemplateColumns: width >= 1400 ? "repeat(4, minmax(240px, 1fr))" : "repeat(auto-fit, minmax(240px, 1fr))",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Title */}
      <SectionHeader title="Zakupy" icon={ShoppingCart} compact={compact} />
      <Separator compact={compact} />

      {/* KPI tiles */}
      <div style={gridStyle}>
        {/* 1) Zakupy netto */}
        <Box compact={compact} sparkline={sparkNet?.length ? <Sparkline data={sparkNet} color={red} height={compact ? 28 : 32} /> : null}>
          <TitleRow compact={compact} icon={<BarChart3 size={compact ? 12 : 14} />} label="Zakupy netto (miesiąc)" />
          <ValueRow compact={compact} value={fmtPLN0(currNet)} delta={netDelta} color={black} invertForPurchases />
        </Box>

        {/* 2) Faktury zakupu – ilość */}
        <Box compact={compact} sparkline={sparkInv?.length ? <Sparkline data={sparkInv} color={red} height={compact ? 28 : 32} /> : null}>
          <TitleRow compact={compact} icon={<FileText size={compact ? 12 : 14} />} label="Faktury zakupu – ilość" hintColor={blue} />
          <ValueRow compact={compact} value={String(currInv)} delta={invDelta} color={blue} invertForPurchases />
        </Box>

        {/* 3) VAT naliczony */}
        <Box compact={compact} sparkline={sparkVatIn?.length ? <Sparkline data={sparkVatIn} color={green} height={compact ? 28 : 32} /> : null}>
          <TitleRow compact={compact} icon={<Calculator size={compact ? 12 : 14} />} label="VAT naliczony" />
          <ValueRow compact={compact} value={fmtPLN0(vatInCurr)} delta={vatInDelta} color={green} />
        </Box>

        {/* 4) Dostawcy – liczba w bieżącym m-cu */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<Users size={compact ? 12 : 14} />} label="Dostawcy (bieżący miesiąc)" hintColor={blue} />
          <ValueRow compact={compact} value={String(currSuppliers)} delta={suppliersDelta} color={blue} />
        </Box>

        {/* 5) Nowi dostawcy – pierwszy raz w historii */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<Users size={compact ? 12 : 14} />} label="Nowi dostawcy (pierwszy raz)" hintColor={blue} />
          <ValueRow compact={compact} value={String(currNewEver)} delta={newSuppliersDelta} color={blue} />
        </Box>

        {/* 6) Udział PL/EU */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<PieChart size={compact ? 12 : 14} />} label="Udział PL/EU" />
          <div style={{ display: "flex", gap: 10, fontSize: compact ? 16 : 18, fontWeight: 800 }}>
            <span style={{ color: black }}>{pct(mix.pl)}</span>
            <span style={{ color: blue }}>{pct(mix.eu)}</span>
          </div>
          <div style={{ fontSize: compact ? 11 : 12, color: "#94a3b8" }}>wg kraju dostawcy</div>
        </Box>

        {/* 7) % koncentracji Top 3 */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<Percent size={compact ? 12 : 14} />} label="% koncentracji Top 3" />
          <div style={{ fontSize: compact ? 20 : 22, fontWeight: 800, color: black }}>{pct(top3)}</div>
          <div style={{ fontSize: compact ? 11 : 12, color: "#94a3b8" }}>udział Top3 dostawców</div>
        </Box>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", marginTop: compact ? 0 : 2 }}>
        <FileText size={compact ? 14 : 16} />
        <span style={{ fontSize: compact ? 11 : 12 }}>
          {asOfText}
        </span>
      </div>
    </div>
  );
};

export default ZakupyKPI;
